import React, { useMemo, useState } from 'react';
import {
  BaseButton,
  ErrorHandler,
  Icon,
  LoadingWrapper,
  Drawer,
  SecondaryButton,
} from '@common/components';
import { useQuery } from '@common/hooks';
import { Button, Col, Row, Select, Tooltip } from 'antd';
import { Tier, TiersQuery } from '@institutions/graphql/queries/tiers-query';
import { SuppliersManagementTiersDrawerTierDetails } from './_suppliers-management-tiers-drawer-tier-details';
import { SuppliersManagementTiersDrawerTierSuppliers } from './_suppliers-management-tiers-drawer-tier-suppliers';
import { SuppliersManagementCreateTierModal } from './_suppliers-management-create-tier-modal';
import { SuppliersManagementUpdateTierModal } from './_suppliers-management-update-tier-modal';
import { SuppliersManagementArchiveTierModal } from './_suppliers-management-archive-tier-modal';

export function SuppliersManagementTiersDrawer({
  open,
  onClose,
  tierId,
}: {
  open: boolean;
  onClose: () => void;
  tierId?: string;
}) {
  const [selectedTierId, setSelectedTierId] = useState<string | undefined>(
    tierId
  );

  const [createTierModalVisible, setCreateTierModalVisible] =
    useState<boolean>(false);
  const [updateTierModalVisible, setUpdateTierModalVisible] =
    useState<boolean>(false);
  const [removeTierModalVisible, setRemoveTierModalVisible] =
    useState<boolean>(false);

  const { data, loading, error, refetch } = useQuery(TiersQuery, {
    onCompleted: (data) => {
      if (!selectedTierId && data.tiers.length > 0) {
        setSelectedTierId(data.tiers[0].id);
      }
    },
  });

  const selectedTier = useMemo(() => {
    if (!data) return;
    const tier = data.tiers.find((tier: Tier) => tier.id === selectedTierId);
    return tier || data.tiers[0];
  }, [data, selectedTierId]);

  const defaultTier = useMemo(() => {
    return data?.tiers[0];
  }, [data]);

  const isDefaultTierSelected = selectedTier?.id === defaultTier?.id;

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="SuppliersManagementTiersDrawer"
        messageType="contextual"
      />
    );
  }

  const onSelect = (value: string) => {
    setSelectedTierId(value);
  };

  const refetchAndSelect = (tierId: string) => {
    refetch().then(() => {
      setSelectedTierId(tierId);
    });
  };

  const ArchiveButton = ({ disabled = false }: { disabled?: boolean }) => {
    return (
      <BaseButton
        type="link"
        danger
        icon={<Icon icon="trashCan" />}
        disabled={disabled}
        onClick={() => {
          setUpdateTierModalVisible(false);
          setRemoveTierModalVisible(true);
        }}
      >
        Remove
      </BaseButton>
    );
  };

  return (
    <Drawer open={open} onClose={onClose} title="Tiers">
      <Row gutter={16}>
        <Col flex="auto">
          <Select
            id="suppliers-management-tiers-drawer__tier-select-id"
            value={selectedTier?.id}
            onSelect={onSelect}
            style={{ width: '100%' }}
            size="large"
            loading={loading}
          >
            {data?.tiers.map((tier) => (
              <Select.Option key={tier.id} value={tier.id}>
                {tier.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col flex="none">
          <SecondaryButton
            size="large"
            icon={<Icon icon="plus" />}
            onClick={() => setCreateTierModalVisible(true)}
          >
            Tier
          </SecondaryButton>
          <SuppliersManagementCreateTierModal
            visible={createTierModalVisible}
            setVisible={setCreateTierModalVisible}
            onCreate={refetchAndSelect}
          />
        </Col>
      </Row>
      <LoadingWrapper loading={loading}>
        <>
          {defaultTier && selectedTier && (
            <>
              <SuppliersManagementTiersDrawerTierDetails
                tier={selectedTier}
                title={
                  <>
                    Details
                    <Button
                      type="link"
                      onClick={() => setUpdateTierModalVisible(true)}
                      style={{ paddingLeft: '8px' }}
                    >
                      <Icon icon="pencil" size="md" />
                    </Button>
                  </>
                }
              />
              <SuppliersManagementUpdateTierModal
                tier={selectedTier}
                visible={updateTierModalVisible}
                setVisible={setUpdateTierModalVisible}
                onUpdate={refetchAndSelect}
                actions={
                  isDefaultTierSelected ? (
                    <Tooltip title="You can't delete the default tier">
                      <span>
                        <ArchiveButton disabled />
                      </span>
                    </Tooltip>
                  ) : (
                    <ArchiveButton />
                  )
                }
              />
              <SuppliersManagementArchiveTierModal
                tier={selectedTier}
                defaultTier={defaultTier}
                visible={removeTierModalVisible}
                setVisible={setRemoveTierModalVisible}
                onArchive={() => refetchAndSelect(defaultTier.id)}
              />
            </>
          )}
        </>
      </LoadingWrapper>
      <SuppliersManagementTiersDrawerTierSuppliers
        loading={loading}
        suppliers={selectedTier?.suppliers || []}
      />
    </Drawer>
  );
}
